import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../../theme';
import { PhoneCard, PhoneCardData } from './PhoneCard';
import { Input, PhoneInput, Button } from '../design-system';
import { useAppStore } from '../../store/appStore';
import { apiService } from '../../services/api';
import { useAppContext } from '../../contexts/AppContext';
import { parseInternationalPhoneNumber } from '../../utils/phoneNumber';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { defaultPhoneCountry } from '../../utils/businessDefaults';

interface PhoneSettingsProps {
  onBack?: () => void;
}

export const PhoneSettings: React.FC<PhoneSettingsProps> = ({ onBack }) => {
  const styles = useThemedStyles(getStyles);
  const { user, setUser } = useAppStore();
  const { showNotification } = useAppContext();
  const [phones, setPhones] = useState<PhoneCardData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [editingPhone, setEditingPhone] = useState<PhoneCardData | null>(null);
  const phoneInputRef = React.useRef<any>(null);
  const [formData, setFormData] = useState({
    phone: '',
    label: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load phones from user profile
  useEffect(() => {
    loadPhones();
  }, [user]);

  const loadPhones = () => {
    if (user?.mobile || user?.phone) {
      const primaryPhone: PhoneCardData = {
        id: 'primary',
        phoneNumber: user.mobile || user.phone || '',
        label: 'Primary',
        isPrimary: true,
        isVerified: true,
      };
      setPhones([primaryPhone]);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddPhone = () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      // Get formatted phone from PhoneInput
      let formattedPhone = formData.phone;
      if (phoneInputRef.current) {
        const phoneData = phoneInputRef.current.getValue();
        if (phoneData?.formattedNumber) {
          const countryCode = phoneInputRef.current.getCountryCode();
          const nationalNumber = phoneData.nationalNumber || phoneData.formattedNumber.replace(/[^\d]/g, '');
          let cleanNumber = nationalNumber;
          if (nationalNumber.startsWith(countryCode)) {
            cleanNumber = nationalNumber.substring(countryCode.length);
          }
          formattedPhone = `${countryCode}${cleanNumber}`;
        }
      }

      // For now, we'll update the primary phone
      // In the future, this can be extended to support multiple phones
      const updateData: any = {
        mobile: formattedPhone,
      };

      apiService.updateProfile(updateData).then((response) => {
        if (response.statusCode === 1000 && response.data) {
          if (setUser) {
            setUser({
              ...user,
              ...response.data,
              mobile: formattedPhone,
              phone: formattedPhone,
            });
          }
          showNotification('success', 'Success', 'Phone number updated successfully!');
          setFormData({ phone: '', label: '' });
          setIsAdding(false);
          loadPhones();
        } else {
          showNotification('error', 'Error', 'Failed to update phone number. Please try again.');
        }
        setIsLoading(false);
      });
    } catch (error: any) {
      console.error('Error updating phone:', error);
      showNotification('error', 'Error', error.message || 'Failed to update phone number.');
      setIsLoading(false);
    }
  };

  const handleEditPhone = (phone: PhoneCardData) => {
    setEditingPhone(phone);
    // Parse phone number to extract national number for display
    let displayPhone = phone.phoneNumber;
    try {
      const phoneToParse = phone.phoneNumber.startsWith('+') ? phone.phoneNumber : `+${phone.phoneNumber}`;
      const parsed = parseInternationalPhoneNumber(phoneToParse);
      if (parsed && parsed.nationalNumber) {
        displayPhone = parsed.nationalNumber;
        // Set country in PhoneInput
        if (phoneInputRef.current && parsed.country) {
          phoneInputRef.current.setCountry(parsed.country);
        }
      }
    } catch (error) {
      // If parsing fails, use as-is
    }
    setFormData({
      phone: displayPhone,
      label: phone.label || '',
    });
    setIsAdding(true);
  };

  const handleDeletePhone = (phoneId: string) => {
    if (phoneId === 'primary') {
      showNotification('warning', 'Warning', 'Cannot delete primary phone number.');
      return;
    }
    // Future: Implement delete logic for additional phones
    showNotification('info', 'Info', 'Delete functionality will be available for additional phone numbers.');
  };

  const handleSetPrimary = (phoneId: string) => {
    // Future: Implement set primary logic
    showNotification('info', 'Info', 'Set primary functionality will be available for additional phone numbers.');
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingPhone(null);
    setFormData({ phone: '', label: '' });
    setErrors({});
  };

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
      showsVerticalScrollIndicator={false}
    >
      {Platform.OS !== 'web' && (
        <View style={styles.header}>
          <Text style={styles.title}>Phone Numbers</Text>
          <Text style={styles.subtitle}>Manage your phone numbers</Text>
        </View>
      )}

      {/* Phone Cards Section */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionLabel}>Your Phone Numbers</Text>
          {!isAdding && (
            <TouchableOpacity
              style={styles.addButton}
              onPress={() => setIsAdding(true)}
              activeOpacity={0.7}
            >
              <Ionicons name="add" size={20} color={colors.white} />
              <Text style={styles.addButtonText}>Add Phone</Text>
            </TouchableOpacity>
          )}
        </View>

        {phones.length > 0 ? (
          phones.map((phone) => (
            <PhoneCard
              key={phone.id}
              phone={phone}
              onEdit={handleEditPhone}
              onDelete={handleDeletePhone}
              onSetPrimary={handleSetPrimary}
            />
          ))
        ) : (
          <View style={styles.emptyState}>
            <Ionicons name="call-outline" size={48} color={colors.text.secondary} />
            <Text style={styles.emptyStateText}>No phone numbers added</Text>
            <Text style={styles.emptyStateSubtext}>Add a phone number to get started</Text>
          </View>
        )}
      </View>

      {/* Add/Edit Phone Form */}
      {isAdding && (
        <View style={styles.section}>
          <View style={styles.formHeader}>
            <Text style={styles.sectionLabel}>
              {editingPhone ? 'Edit Phone Number' : 'Add Phone Number'}
            </Text>
            <TouchableOpacity onPress={handleCancel} activeOpacity={0.7}>
              <Ionicons name="close" size={24} color={colors.text.secondary} />
            </TouchableOpacity>
          </View>

          <Input
            label="Label (Optional)"
            placeholder="e.g., Work, Home, Mobile"
            value={formData.label}
            onChangeText={(text) => {
              setFormData(prev => ({ ...prev, label: text }));
            }}
            size="md"
            variant="outlined"
            state="default"
            editable={!isLoading}
          />

          <PhoneInput
            ref={phoneInputRef}
            label="Phone Number"
            value={formData.phone}
            onChangeText={(text) => {
              setFormData(prev => ({ ...prev, phone: text }));
              if (errors.phone) {
                setErrors(prev => ({ ...prev, phone: '' }));
              }
            }}
            placeholder="Enter phone number"
            error={errors.phone}
            size="md"
            variant="outlined"
            state={errors.phone ? 'error' : 'default'}
            disabled={isLoading}
            initialCountry={defaultPhoneCountry()}
          />

          <View style={styles.formActions}>
            <Button
              title="Cancel"
              onPress={handleCancel}
              variant="outline"
              size="md"
              style={styles.cancelButton}
              disabled={isLoading}
            />
            <Button
              title={editingPhone ? 'Update' : 'Add Phone'}
              onPress={handleAddPhone}
              loading={isLoading}
              variant="primary"
              size="md"
              style={styles.saveButton}
            />
          </View>
        </View>
      )}
    </ScrollView>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  contentContainer: {
    padding: spacing.lg,
    paddingBottom: spacing.xl * 2,
  },
  header: {
    marginBottom: spacing.xl,
  },
  title: {
    fontSize: typography.fontSize['2xl'],
    fontFamily: typography.fontFamily.bold,
    color: colors.text.primary,
    marginBottom: spacing.xs,
  },
  subtitle: {
    fontSize: typography.fontSize.base,
    color: colors.text.secondary,
  },
  section: {
    marginBottom: spacing.xl,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.md,
  },
  sectionLabel: {
    fontSize: typography.fontSize.sm,
    textTransform: 'uppercase',
    color: colors.text.secondary,
    letterSpacing: 0.8,
    fontFamily: typography.fontFamily.semibold,
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: spacing.borderRadius.md,
    backgroundColor: colors.primary,
  },
  addButtonText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.semibold,
    color: colors.white,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.xl * 2,
    paddingHorizontal: spacing.lg,
  },
  emptyStateText: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
    marginTop: spacing.md,
  },
  emptyStateSubtext: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    marginTop: spacing.xs,
    textAlign: 'center',
  },
  formHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.md,
  },
  formActions: {
    flexDirection: 'row',
    gap: spacing.md,
    marginTop: spacing.lg,
  },
  cancelButton: {
    flex: 1,
  },
  saveButton: {
    flex: 1,
  },
});
